import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  Divider,
  Tooltip,
  Drawer,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button as MuiButton
} from '@mui/material';
import { 
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  History
} from '@mui/icons-material';
import { uploadInvoice, reUploadRejectedInvoice, getMyInvoices, getDownloadUrl, getTodos, getInvoiceAuditLogs } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import SupplierProfileForm from './SupplierProfileForm';

const SupplierDashboard = () => {
  const { user } = useAuth0();
  const [invoices, setInvoices] = useState([]);
  const [todos, setTodos] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('dashboard');
  const [isUploading, setIsUploading] = useState(false);

  // Create a combined list of todos with their matching invoices
  const [combinedData, setCombinedData] = useState([]);

  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditTodo, setSelectedAuditTodo] = useState(null);

  useEffect(() => {
    fetchTodos();
    fetchInvoices();
  }, []);

  // When todos or invoices change, update the combined data
  useEffect(() => {
    const combined = todos.map(todo => {
      // Find matching invoice if any
      const matchingInvoice = invoices.find(
        invoice => invoice.supplier_name === todo.supplier_name && invoice.month_year === todo.month_year
      );
      
      return {
        ...todo,
        invoice: matchingInvoice || null
      };
    });
    
    setCombinedData(combined);
    
    // Select the first incomplete todo by default if none is selected
    if (!selectedTodo && combined.length > 0) {
      const incompleteTodo = combined.find(item => !item.completed);
      if (incompleteTodo) {
        setSelectedTodo(incompleteTodo);
      }
    }
  }, [todos, invoices]);

  const fetchTodos = async () => {
    try {
      const response = await getTodos();
      setTodos(response.data);
    } catch (error) {
      console.error('Error fetching todos:', error);
      showSnackbar('Failed to fetch todos', 'error');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await getMyInvoices();
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showSnackbar('Failed to fetch invoices', 'error');
    }
  };

  const handleFileUpload = async (event) => {
    if (!selectedTodo) {
      showSnackbar('Please select a task first', 'error');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_name', selectedTodo.supplier_name);
    formData.append('month_year', selectedTodo.month_year);

    try {
      await uploadInvoice(formData);
      showSnackbar('Invoice uploaded successfully', 'success');
      fetchInvoices();
      fetchTodos(); // Refresh todos to update completion status
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar(`Failed to upload invoice: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setIsUploading(false);
      event.target.value = null; // Reset file input
    }
  };

  const handleDownload = async (item) => {
    if (!item.invoice || !item.invoice.file_key) {
      showSnackbar('No invoice available to download', 'error');
      return;
    }
    
    try {
      const response = await getDownloadUrl(item.supplier_name, item.month_year);
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      console.error('Error getting download URL:', error);
      showSnackbar(`Failed to get download URL: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleReUploadInvoice = async (event, item) => {
    if (!item.invoice) {
      showSnackbar('No invoice to replace', 'error');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_name', item.supplier_name);
    formData.append('month_year', item.month_year);
    formData.append('previous_file_key', item.invoice.file_key);
    formData.append('is_replacement', 'true');

    try {
      await reUploadRejectedInvoice(formData);
      showSnackbar('Invoice re-uploaded successfully', 'success');
      fetchInvoices();
      fetchTodos(); // Refresh todos to update completion status
    } catch (error) {
      console.error('Error re-uploading file:', error);
      showSnackbar(`Failed to re-upload invoice: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setIsUploading(false);
      event.target.value = null; // Reset file input
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ 
      open: true, 
      message, 
      severity 
    });
  };

  const getStatusChip = (item) => {
    // If invoice is paid
    if (item.invoice && item.invoice.status === 'paid') {
      return <Chip label="Paid" color="success" size="small" />;
    }
    // If completed but not paid
    if (item.completed) {
      return <Chip label="Completed" color="success" size="small" />;
    }
    // If invoice exists
    if (item.invoice) {
      if (item.invoice.status === 'approved') {
        return <Chip label="Approved" color="success" size="small" />;
      } else if (item.invoice.status === 'hold') {
        return <Chip label="On Hold" color="warning" size="small" />;
      } else if (item.invoice.status === 'rejected') {
        return (
          <Tooltip 
            title={
              <Typography variant="body2">
                Rejection reason: {item.rejection_reason || item.invoice?.rejection_reason || 'No reason provided'}
              </Typography>
            }
            arrow
            placement="top"
          >
            <Chip label="Rejected" color="error" size="small" />
          </Tooltip>
        );
      } else if (item.invoice.status === 'uploaded') {
        return <Chip label="Uploaded" color="info" size="small" />;
      }
    }
    // Default: no invoice
    return <Chip label="Pending Upload" color="warning" size="small" />;
  };

  const handleOpenAuditDrawer = async (item) => {
    setSelectedAuditTodo(item);
    setAuditDrawerOpen(true);
    setAuditLoading(true);
    try {
      const res = await getInvoiceAuditLogs(item.supplier_name, item.month_year);
      setAuditLogs(res.data);
    } catch (err) {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCloseAuditDrawer = () => {
    setAuditDrawerOpen(false);
    setAuditLogs([]);
    setSelectedAuditTodo(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', color: 'primary.main' }}>
          Welcome back, {user?.name || 'Supplier'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Manage your invoices and tasks all in one place
        </Typography>
      </Box>

      {/* View Selection */}
      <Paper 
        elevation={2} 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 4, 
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="view-mode"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <FormControlLabel 
              value="dashboard" 
              control={<Radio color="primary" />} 
              label="Dashboard" 
            />
            <FormControlLabel 
              value="profile" 
              control={<Radio color="primary" />} 
              label="Profile" 
            />
          </RadioGroup>
        </FormControl>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchTodos();
            fetchInvoices();
          }}
          variant="outlined"
          sx={{ borderRadius: '8px', textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Paper>

      {/* Main Content */}
      {viewMode === 'dashboard' && (
        <Paper 
          elevation={2} 
          variant="outlined" 
          sx={{ 
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!selectedTodo || isUploading || (selectedTodo && selectedTodo.invoice)}
              startIcon={<UploadIcon />}
              component="label"
              sx={{ 
                borderRadius: '8px', 
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload Invoice'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Select</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Month/Year</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Original Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Deduction</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Final Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem', bgcolor: '#f8fafc' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedData.map((item) => (
                  <TableRow 
                    key={item.id}
                    hover
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      bgcolor: selectedTodo?.id === item.id ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                    }}
                  >
                    <TableCell sx={{ py: 2, width: '80px' }}>
                      <Radio
                        checked={selectedTodo?.id === item.id}
                        onChange={() => setSelectedTodo(item)}
                        disabled={item.completed}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{item.title}</TableCell>
                    <TableCell sx={{ py: 2 }}>{item.month_year}</TableCell>
                    <TableCell sx={{ py: 2 }}>{item.description}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.amount !== null ? `$${item.amount.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.deduction > 0 ? `$${item.deduction.toFixed(2)}` : '$0.00'}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.total_after_deduction !== null ? `$${item.total_after_deduction.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.status === 'approved' && item.expected_payment_date ? (
                        <Tooltip title={`Expected Payment: ${new Date(item.expected_payment_date).toLocaleDateString()}`} arrow>
                          <span>{getStatusChip(item)}</span>
                        </Tooltip>
                      ) : getStatusChip(item)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Download button - show if there is an invoice */}
                        {item.invoice && (
                          <Tooltip title="Download Invoice">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleDownload(item)}
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(25, 118, 210, 0.1)', 
                                '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {/* Re-upload button - only for rejected invoices */}
                        {item.invoice && item.invoice.status === 'rejected' && (
                          <>
                            <input
                              accept=".pdf,.doc,.docx"
                              id={`re-upload-file-${item.id}`}
                              type="file"
                              hidden
                              onChange={(e) => handleReUploadInvoice(e, item)}
                            />
                            <label htmlFor={`re-upload-file-${item.id}`}>
                              <Tooltip title="Replace Rejected Invoice">
                                <IconButton 
                                  component="span"
                                  color="success" 
                                  size="small"
                                  sx={{ 
                                    bgcolor: 'rgba(76, 175, 80, 0.1)', 
                                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' }
                                  }}
                                  disabled={isUploading}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </label>
                          </>
                        )}

                        {/* Upload button - only for todos without invoices */}
                        {!item.invoice && !item.completed && (
                          <>
                            <input
                              accept=".pdf,.doc,.docx"
                              id={`upload-file-${item.id}`}
                              type="file"
                              hidden
                              onChange={handleFileUpload}
                            />
                            <label htmlFor={`upload-file-${item.id}`}>
                              <Tooltip title="Upload Invoice">
                                <IconButton 
                                  component="span"
                                  color="primary" 
                                  size="small"
                                  sx={{ 
                                    bgcolor: 'rgba(25, 118, 210, 0.1)', 
                                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
                                  }}
                                  disabled={isUploading || selectedTodo?.id !== item.id}
                                >
                                  <UploadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </label>
                          </>
                        )}

                        <Tooltip title="View Audit Log">
                          <IconButton
                            color="info"
                            onClick={() => handleOpenAuditDrawer(item)}
                            size="small"
                            sx={{ bgcolor: 'rgba(2, 136, 209, 0.1)', '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.2)' } }}
                          >
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {combinedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No tasks found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {viewMode === 'profile' && (
        <SupplierProfileForm />
      )}

      <Drawer anchor="right" open={auditDrawerOpen} onClose={handleCloseAuditDrawer}>
        <Box sx={{ width: 400, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Audit Log
          </Typography>
          {auditLoading ? (
            <CircularProgress />
          ) : auditLogs.length === 0 ? (
            <Typography>No audit logs found.</Typography>
          ) : (
            <Stepper orientation="vertical" nonLinear activeStep={-1}>
              {auditLogs.map((log, idx) => (
                <Step key={log.id} completed>
                  <StepLabel>{`${log.action} by ${log.performed_by} at ${new Date(log.performed_at).toLocaleString()} Filename: ${log.details.file_name}`}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          <MuiButton onClick={handleCloseAuditDrawer} sx={{ mt: 2 }} fullWidth variant="outlined">Close</MuiButton>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupplierDashboard; 